/**
 * The call surface: a pre-join step, then the meeting itself, both in one
 * dialog.
 *
 * A meeting is not a notification strip. Joining one is a decision — is my
 * camera on, is the right microphone picked, does the browser even let me,
 * and who is in there already — and that decision belongs in front of you
 * before anyone's phone rings, not in a corner afterwards. Every way in goes
 * through it: starting one, answering a ring, pressing Join on the bar.
 *
 * See _design/sketches/Meetings.md for the copy and the measurements.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'

import type { Call, UUID } from '@/api/types'
import { describeCall } from '@/hooks/callPeers'
import type { CallPreferences, CallSessionState, StartShareOptions } from '@/hooks/useCallSession'
import { Button, Checkbox, Modal, Select } from '@/components/ui'
import {
  CallGrid,
  CallPeople,
  CallPresentation,
  participantName,
  stageTiles,
  surfaceNoun,
} from '@/components/CallStageView'
import styles from './CallWindow.module.css'

export type CallStage =
  | { kind: 'prejoin'; title: string; media: 'audio' | 'video'; call?: Call | null; context?: string }
  | { kind: 'in-call'; title: string; call: Call }

export type CallSessionApi = CallSessionState & {
  setMicEnabled: (on: boolean) => void
  setCameraEnabled: (on: boolean) => void
  startShare: (options?: StartShareOptions) => Promise<void>
  stopShare: () => Promise<void>
  dismissShareError: () => void
}

interface CallWindowProps {
  stage: CallStage | null
  selfUserId?: UUID
  session: CallSessionApi
  preferences: CallPreferences
  onPreferencesChange: (preferences: CallPreferences) => void
  joining: boolean
  onJoin: () => void
  onCancel: () => void
  onHangUp: () => void
  /**
   * Put the meeting away without leaving it. Closing the window is not
   * hanging up — the only way out of a call is Leave call.
   */
  onMinimise?: () => void
  /** The server implements calls-v2. Without it, sharing is absent, not disabled. */
  screenSharingEnabled?: boolean
}

/**
 * The preview is the point of the pre-join step: it is the only way to find
 * out that the browser said no, or that the camera is pointing at the ceiling,
 * before other people are involved.
 */
function useLocalPreview(
  active: boolean,
  camera: boolean,
  audioDeviceId: string | undefined,
  videoDeviceId: string | undefined,
): {
  stream: MediaStream | null
  error: string | null
  devices: MediaDeviceInfo[]
} {
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])

  useEffect(() => {
    if (!active) {
      setStream(null)
      setError(null)
      return undefined
    }

    let cancelled = false
    let opened: MediaStream | null = null

    void navigator.mediaDevices
      .getUserMedia({
        audio: audioDeviceId === undefined ? true : { deviceId: { exact: audioDeviceId } },
        video: !camera ? false : videoDeviceId === undefined ? true : { deviceId: { exact: videoDeviceId } },
      })
      .then((media) => {
        if (cancelled) {
          media.getTracks().forEach((track) => track.stop())
          return
        }
        opened = media
        setStream(media)
        setError(null)
        // Device names are only readable once a stream is open; before that
        // the browser lists unlabelled entries nobody can choose between.
        if (typeof navigator.mediaDevices.enumerateDevices === 'function') {
          void navigator.mediaDevices
            .enumerateDevices()
            .then((list) => {
              if (!cancelled) setDevices(list)
            })
            .catch(() => undefined)
        }
      })
      .catch((caught: unknown) => {
        if (cancelled) return
        setStream(null)
        setError(
          caught instanceof Error && caught.name === 'NotAllowedError'
            ? 'Your browser is blocking the camera and microphone for this Space. Allow them in the address bar, then try again.'
            : 'No camera or microphone could be opened.',
        )
      })

    return () => {
      cancelled = true
      opened?.getTracks().forEach((track) => track.stop())
    }
  }, [active, audioDeviceId, camera, videoDeviceId])

  return { stream, error, devices }
}

function PreviewVideo({ stream }: { stream: MediaStream }): ReactElement {
  const ref = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    if (ref.current !== null) ref.current.srcObject = stream
  }, [stream])

  return <video ref={ref} className={styles.previewVideo} autoPlay playsInline muted />
}

/** How long the call has been up, from the server's own clock. */
function useElapsed(startedAt: string | null): string {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  if (startedAt === null) return ''
  const seconds = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000))
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const pad = (value: number): string => String(value).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes % 60)}:${pad(seconds % 60)}` : `${minutes}:${pad(seconds % 60)}`
}

/** Who is already in, and what they are doing — the line that decides it. */
function joinedNames(call: Call | null | undefined, selfUserId: UUID | undefined): string[] {
  if (call === null || call === undefined) return []
  return call.participants
    .filter((participant) => participant.status === 'joined' && participant.user?.id !== selfUserId)
    .map((participant) => {
      const person = participant.user
      if (person === null || person === undefined) return 'Someone'
      const display = person.display_name.trim()
      return display === '' ? person.username : display
    })
}

export function listNames(names: string[]): string {
  if (names.length === 0) return ''
  if (names.length === 1) return names[0] ?? ''
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`
  return `${names[0]} and ${names.length - 1} more`
}

export function CallWindow({
  stage,
  selfUserId,
  session,
  preferences,
  onPreferencesChange,
  joining,
  onJoin,
  onCancel,
  onHangUp,
  onMinimise,
  screenSharingEnabled = false,
}: CallWindowProps): ReactElement | null {
  const [showPeople, setShowPeople] = useState(false)
  const prejoin = stage?.kind === 'prejoin'
  const cameraOffered = stage?.kind === 'prejoin' && stage.media === 'video'

  const preview = useLocalPreview(
    prejoin,
    cameraOffered && preferences.camera,
    preferences.audioDeviceId,
    preferences.videoDeviceId,
  )

  const microphones = useMemo(
    () => preview.devices.filter((device) => device.kind === 'audioinput'),
    [preview.devices],
  )
  const cameras = useMemo(() => preview.devices.filter((device) => device.kind === 'videoinput'), [preview.devices])

  const call = stage === null ? null : stage.kind === 'in-call' ? stage.call : (stage.call ?? null)
  const elapsed = useElapsed(stage?.kind === 'in-call' ? stage.call.started_at : null)

  // A meeting that is already three people deep should not be joined by a
  // fourth microphone nobody asked for.
  const alreadyIn = joinedNames(call, selfUserId)
  const busyRoom = alreadyIn.length >= 3
  useEffect(() => {
    if (prejoin && busyRoom && preferences.mic) {
      onPreferencesChange({ ...preferences, mic: false })
    }
    // Only when the dialog opens on a busy call; afterwards the person decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyRoom, prejoin])

  if (stage === null) return null

  if (stage.kind === 'prejoin') {
    const sharingName =
      call !== null && call.sharing_user_id != null ? participantName(call, call.sharing_user_id) : null

    return (
      <Modal
        open
        onClose={onCancel}
        title={stage.title}
        description={stage.context}
        size="md"
        closeOnOverlayClick={false}
        footer={
          <>
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" loading={joining} onClick={onJoin}>
              Join
            </Button>
          </>
        }
      >
        <div className={styles.prejoin}>
          <div className={styles.preview}>
            {cameraOffered && preferences.camera && preview.stream !== null ? (
              <PreviewVideo stream={preview.stream} />
            ) : (
              <p className={styles.placeholder}>
                {cameraOffered && preferences.camera ? 'Opening the camera…' : 'Camera off'}
              </p>
            )}
            {/* The one sentence people want confirmed before anyone sees them. */}
            {!cameraOffered || !preferences.camera ? (
              <span className={styles.previewState}>
                <span className={styles.previewDot} />
                Camera off · nobody sees you yet
              </span>
            ) : null}
          </div>

          {/* Whichever went wrong first: the browser refusing the devices, or
              the server refusing the call. */}
          {(preview.error ?? session.error) === null ? null : (
            <p className={styles.error} role="alert">
              {preview.error ?? session.error}
            </p>
          )}

          {alreadyIn.length === 0 ? null : (
            <div className={styles.roster}>
              <div className={styles.rosterText}>
                <span className={styles.rosterLine}>
                  {listNames(alreadyIn)} {alreadyIn.length === 1 ? 'is' : 'are'} in
                </span>
                <span className={styles.rosterMeta}>
                  {sharingName === null ? '' : `${sharingName} is sharing a screen · `}
                  {alreadyIn.length + 1} of 8 seats
                </span>
              </div>
            </div>
          )}

          <div className={styles.toggles}>
            <Button
              size="sm"
              variant={preferences.mic ? 'secondary' : 'ghost'}
              aria-pressed={preferences.mic}
              onClick={() => onPreferencesChange({ ...preferences, mic: !preferences.mic })}
            >
              {preferences.mic ? 'Microphone on' : 'Microphone off'}
            </Button>
            {cameraOffered ? (
              <Button
                size="sm"
                variant={preferences.camera ? 'secondary' : 'ghost'}
                aria-pressed={preferences.camera}
                onClick={() => onPreferencesChange({ ...preferences, camera: !preferences.camera })}
              >
                {preferences.camera ? 'Camera on' : 'Camera off'}
              </Button>
            ) : null}
          </div>

          {/* Which microphone and camera, once the browser lets us name them.
              A laptop with a headset plugged in has two of each, and the
              default is the wrong one often enough to ask. */}
          {microphones.length > 1 || cameras.length > 1 ? (
            <div className={styles.devices}>
              {microphones.length > 1 ? (
                <Select
                  label="Microphone"
                  value={preferences.audioDeviceId ?? ''}
                  onChange={(event) =>
                    onPreferencesChange({
                      ...preferences,
                      audioDeviceId: event.target.value === '' ? undefined : event.target.value,
                    })
                  }
                  options={[
                    { value: '', label: 'Default microphone' },
                    ...microphones.map((device, index) => ({
                      value: device.deviceId,
                      label: device.label.trim() === '' ? `Microphone ${index + 1}` : device.label,
                    })),
                  ]}
                />
              ) : null}
              {cameraOffered && cameras.length > 1 ? (
                <Select
                  label="Camera"
                  value={preferences.videoDeviceId ?? ''}
                  onChange={(event) =>
                    onPreferencesChange({
                      ...preferences,
                      videoDeviceId: event.target.value === '' ? undefined : event.target.value,
                    })
                  }
                  options={[
                    { value: '', label: 'Default camera' },
                    ...cameras.map((device, index) => ({
                      value: device.deviceId,
                      label: device.label.trim() === '' ? `Camera ${index + 1}` : device.label,
                    })),
                  ]}
                />
              ) : null}
            </div>
          ) : null}

          <div className={styles.options}>
            {screenSharingEnabled && session.share.supported ? (
              <Checkbox
                label="Share a screen as I join"
                hint="You pick what to share in the next step. Nothing goes out before you have seen it."
                checked={preferences.shareOnJoin === true}
                onChange={(event) => onPreferencesChange({ ...preferences, shareOnJoin: event.target.checked })}
              />
            ) : null}
            <Checkbox
              label="Join muted"
              hint={busyRoom ? 'Ticked because three or more are already in.' : undefined}
              checked={!preferences.mic}
              onChange={(event) => onPreferencesChange({ ...preferences, mic: !event.target.checked })}
            />
          </div>

          <p className={styles.reassurance}>This meeting runs directly between the machines.</p>
        </div>
      </Modal>
    )
  }

  const tiles = stageTiles(stage.call, session, selfUserId)
  const status = describeCall(session.peerStates, session.connected)
  const sharing = session.share.state
  const sharingSelf = sharing !== null && sharing.userId === selfUserId
  const surface = sharingSelf ? session.share.local : session.share.remote
  const presenting = sharing !== null && surface !== null
  const hasCamera = session.localStream !== null && session.localStream.getVideoTracks().length > 0
  const canShare = screenSharingEnabled && session.share.supported

  return (
    <Modal
      open
      onClose={onMinimise ?? onHangUp}
      title={stage.title}
      description={
        <span className={styles.status}>
          <span
            className={styles.statusDot}
            data-state={status === 'Connected' ? undefined : status.startsWith('Waiting') ? 'waiting' : 'trouble'}
          />
          {status}
          {elapsed === '' ? '' : ` · ${elapsed}`}
        </span>
      }
      size="lg"
      closeOnOverlayClick={false}
      footer={
        <>
          <Button
            size="sm"
            variant={session.micEnabled ? 'secondary' : 'ghost'}
            aria-pressed={session.micEnabled}
            onClick={() => session.setMicEnabled(!session.micEnabled)}
          >
            {session.micEnabled ? 'Mute' : 'Unmute'}
          </Button>
          {hasCamera ? (
            <Button
              size="sm"
              variant={session.cameraEnabled ? 'secondary' : 'ghost'}
              aria-pressed={session.cameraEnabled}
              onClick={() => session.setCameraEnabled(!session.cameraEnabled)}
            >
              {session.cameraEnabled ? 'Stop video' : 'Start video'}
            </Button>
          ) : null}
          {/* While the takeover question is on screen it is the only place to
              answer it: two buttons with the same name, one of them the
              question and one of them the thing that asked it, is a trap. */}
          {canShare && !session.share.conflict ? (
            <Button
              size="sm"
              variant={sharingSelf ? 'primary' : 'ghost'}
              onClick={() => {
                if (sharingSelf) void session.stopShare()
                else void session.startShare()
              }}
            >
              {sharingSelf ? 'Stop sharing' : sharing === null ? 'Share screen' : 'Share instead'}
            </Button>
          ) : null}
          <Button size="sm" variant={showPeople ? 'secondary' : 'ghost'} aria-pressed={showPeople} onClick={() => setShowPeople((open) => !open)}>
            People · {tiles.length}
          </Button>
          <Button variant="danger" onClick={onHangUp}>
            Leave call
          </Button>
        </>
      }
    >
      <div className={styles.stage}>
        {/* Someone else holds the surface and we asked for it. The takeover is
            a question, never a race. */}
        {session.share.conflict ? (
          <div className={styles.notice} role="alert">
            <span className={styles.noticeText}>
              {sharing === null ? 'Someone else' : participantName(stage.call, sharing.userId)} is sharing a{' '}
              {surfaceNoun(sharing?.source)}. Share now and you take the stage from them.
            </span>
            <Button size="sm" variant="ghost" onClick={session.dismissShareError}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                session.dismissShareError()
                void session.startShare({ takeover: true })
              }}
            >
              Share instead
            </Button>
          </div>
        ) : null}

        {session.share.error !== null && !session.share.conflict ? (
          <div className={styles.notice} role="status">
            <span className={styles.noticeText}>{session.share.error}</span>
            <Button size="sm" variant="ghost" onClick={session.dismissShareError}>
              Dismiss
            </Button>
          </div>
        ) : null}

        {/* Audio is the meeting; video is decoration. When the line narrows,
            the camera goes and the client says so. */}
        {session.videoPausedForBandwidth ? (
          <div className={styles.notice} role="status">
            <span className={styles.noticeText}>The line is narrow. Your camera is off so the audio holds.</span>
            <Button size="sm" variant="ghost" onClick={() => session.setCameraEnabled(true)}>
              Turn on
            </Button>
          </div>
        ) : null}

        <div className={styles.body}>
          {presenting ? (
            <CallPresentation
              surface={surface}
              presenter={sharing === null ? 'Someone' : participantName(stage.call, sharing.userId)}
              source={sharing?.source}
              own={sharingSelf}
              tiles={tiles}
              // The rail and the people panel are two views of the same
              // people. Both at once leaves the shared screen a sliver of
              // what it is for, so the panel wins while it is open: it says
              // more about the same faces.
              rail={!showPeople}
            />
          ) : (
            <CallGrid tiles={tiles} />
          )}
          {showPeople ? <CallPeople tiles={tiles} total={tiles.length} onClose={() => setShowPeople(false)} /> : null}
        </div>

        {Object.keys(session.peerStates).length === 0 && selfUserId !== undefined ? (
          <p className={styles.waiting}>Waiting for the others to join.</p>
        ) : null}
        {session.error === null ? null : (
          <p className={styles.error} role="alert">
            {session.error}
          </p>
        )}
      </div>
    </Modal>
  )
}

/**
 * The prompt for a call someone else started. Three choices, not two: joining
 * without a camera is a decision people make at the door, not a setting they
 * go looking for afterwards.
 */
export function IncomingCallPrompt({
  call,
  where,
  busyIn,
  onAccept,
  onAcceptAudioOnly,
  onDecline,
}: {
  call: Call
  /** The room it is in, for the line under the name. */
  where?: string
  /** The meeting this would hang up on, when there is one. */
  busyIn?: string | null
  onAccept: () => void
  onAcceptAudioOnly: () => void
  onDecline: () => void
}): ReactElement {
  const initiator = call.initiator
  const name =
    initiator === null ? 'Someone' : initiator.display_name.trim() === '' ? initiator.username : initiator.display_name
  const kind = call.media === 'video' ? 'Video meeting' : 'Audio call'
  const busy = busyIn !== null && busyIn !== undefined && busyIn !== ''

  return (
    <div className={styles.banner} role="status" aria-live="assertive">
      <div className={styles.bannerHead}>
        <div className={styles.bannerText}>
          <span className={styles.bannerName}>{name}</span>
          <span className={styles.bannerWhere}>{where === undefined ? kind : `${kind} in ${where}`}</span>
        </div>
      </div>

      {busy ? (
        <p className={styles.bannerBusy}>You’re in a meeting in {busyIn}. Answering here hangs up there.</p>
      ) : null}

      <div className={styles.actions}>
        <Button size="sm" variant="ghost" onClick={onDecline}>
          Decline
        </Button>
        {call.media === 'video' ? (
          <Button size="sm" variant="secondary" onClick={onAcceptAudioOnly}>
            Audio only
          </Button>
        ) : null}
        <Button size="sm" variant="primary" onClick={onAccept}>
          {busy ? 'Leave and join' : 'Join'}
        </Button>
      </div>
    </div>
  )
}
